import React, { useState } from 'react';
import Lottie from 'react-lottie';
import { FaHandPointRight, FaChair, FaFolderOpen, FaBook, FaLeaf, FaPrint } from 'react-icons/fa';
import magnifyAnimationData from './lottie/magnify.json';
import successAnimationData from './lottie/success.json';
import failAnimationData from './lottie/fail.json';

// Define office items with positions and an associated icon.
const officeItems = [
  { id: 1, name: "Desk", left: "10%", top: "30%", icon: <FaChair size={24} className="text-blue-500" /> },
  { id: 2, name: "Filing Cabinet", left: "60%", top: "20%", icon: <FaFolderOpen size={24} className="text-green-500" /> },
  { id: 3, name: "Bookshelf", left: "30%", top: "50%", icon: <FaBook size={24} className="text-purple-500" /> },
  { id: 4, name: "Plant", left: "80%", top: "60%", icon: <FaLeaf size={24} className="text-green-700" /> },
  { id: 5, name: "Printer", left: "40%", top: "10%", icon: <FaPrint size={24} className="text-red-500" /> },
];

const CatchTheFraudsterGame = ({ onBack }) => {
  const [gameStatus, setGameStatus] = useState("idle"); // "idle", "running", "found", "failed"
  const [attempts, setAttempts] = useState(2);
  const [searchMode, setSearchMode] = useState(false);
  const [hiddenCashId, setHiddenCashId] = useState(null);
  const [message, setMessage] = useState("");

  // Lottie options for the magnifying glass animation.
  const magnifyOptions = {
    loop: true,
    autoplay: true,
    animationData: magnifyAnimationData,
    rendererSettings: { preserveAspectRatio: "xMidYMid slice" }
  };

  // Lottie options for success and fail animations.
  const successOptions = {
    loop: false,
    autoplay: true,
    animationData: successAnimationData,
    rendererSettings: { preserveAspectRatio: "xMidYMid slice" }
  };

  const failOptions = {
    loop: false,
    autoplay: true,
    animationData: failAnimationData,
    rendererSettings: { preserveAspectRatio: "xMidYMid slice" }
  };

  // Start or restart the game.
  const startGame = () => {
    setGameStatus("running");
    setAttempts(2);
    setMessage("");
    setSearchMode(false);
    // Randomly choose one office item to hide the cash stash.
    const randomIndex = Math.floor(Math.random() * officeItems.length);
    setHiddenCashId(officeItems[randomIndex].id);
  };

  const restartGame = () => {
    startGame();
  };

  // Toggle search mode on/off.
  const toggleSearchMode = () => {
    if (gameStatus !== "running") return;
    setSearchMode(!searchMode);
  };

  // When a user clicks on an office item (active only in search mode).
  const handleItemClick = (itemId) => {
    if (gameStatus !== "running" || !searchMode) return;
    if (itemId === hiddenCashId) {
      setGameStatus("found");
      setMessage("Congratulations! You found the hidden cash stash 💰!");
    } else {
      const remaining = attempts - 1;
      setAttempts(remaining);
      if (remaining <= 0) {
        setGameStatus("failed");
        setMessage("Game Over! You failed to find the cash stash.");
      } else {
        setMessage("No cash here. Try again!");
      }
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow rounded-lg my-6">
      <h1 className="text-2xl font-bold text-center mb-4">Find the Hidden Cash Game</h1>
      <button onClick={onBack} className="mb-4 text-blue-500 hover:underline">
        &larr; Back to Menu
      </button>

      {gameStatus === "idle" && (
        <div className="text-center">
          <button
            onClick={startGame}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Start Game
          </button>
        </div>
      )}

      {gameStatus === "running" && (
        <>
          <div className="flex justify-between items-center mb-4">
            <p className="font-semibold">Attempts Left: {attempts}</p>
            <button
              onClick={toggleSearchMode}
              className="flex items-center bg-gray-200 px-3 py-1 rounded hover:bg-gray-300"
            >
              {/* Magnifying glass animation with a hand icon overlay */}
              <div className="relative">
                <Lottie options={magnifyOptions} height={50} width={50} isClickToPauseDisabled />
                <FaHandPointRight size={32} className="absolute -bottom-2 -right-2 text-gray-700" />
              </div>
              <span className="ml-2">{searchMode ? "Searching..." : "Use Magnifying Glass"}</span>
            </button>
          </div>

          {/* Office scene with a nice gradient background and office item icons */}
          <div className="relative w-full h-96 bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300 rounded shadow-lg">
            {officeItems.map((item) => (
              <div
                key={item.id}
                className="absolute flex flex-col items-center p-2 bg-white bg-opacity-80 border border-gray-200 rounded cursor-pointer hover:shadow-lg transition"
                style={{ left: item.left, top: item.top }}
                onClick={() => handleItemClick(item.id)}
              >
                {item.icon}
                <span className="text-xs font-medium mt-1">{item.name}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {(gameStatus === "found" || gameStatus === "failed") && (
        <div className="text-center mt-4">
          {gameStatus === "found" ? (
            <Lottie options={successOptions} height={150} width={150} isClickToPauseDisabled />
          ) : (
            <Lottie options={failOptions} height={150} width={150} isClickToPauseDisabled />
          )}
          <h2 className="text-xl font-bold mb-2">
            {gameStatus === "found" ? "You Found the Cash!" : "Game Over!"}
          </h2>
          <p className="mb-4">{message}</p>
          <button
            onClick={restartGame}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Restart Game
          </button>
        </div>
      )}

      {gameStatus === "running" && (
        <div className="mt-4 text-center">
          <p className="text-gray-600">
            Activate the magnifying glass and click on the office item where you think the cash stash is hidden.
          </p>
          {message && <p className="mt-2 text-red-500">{message}</p>}
        </div>
      )}
    </div>
  );
};

export default CatchTheFraudsterGame;
